import * as React from 'react'
import { cn } from '../../lib/cn'
import type { PipelineTerminalData } from './types'

export interface PipelineTerminalProps extends React.HTMLAttributes<HTMLDivElement> {
  terminal: PipelineTerminalData
}

interface ToneTokens {
  background: string
  borderColor: string
  color: string
  boxShadow?: string
  labelColor: string
}

const TONE_TOKENS: Record<'neutral' | 'allow' | 'deny', ToneTokens> = {
  neutral: {
    background: 'var(--pe-bg-card)',
    borderColor: 'var(--pe-border-card)',
    color: 'var(--pe-text-tertiary)',
    labelColor: 'var(--pe-text-secondary)',
  },
  allow: {
    background: 'color-mix(in srgb, var(--pe-accent-terminal) 8%, transparent)',
    borderColor: 'color-mix(in srgb, var(--pe-accent-terminal) 35%, transparent)',
    color: 'var(--pe-accent-terminal)',
    boxShadow: '0 0 24px color-mix(in srgb, var(--pe-accent-terminal) 15%, transparent)',
    labelColor: 'var(--pe-accent-terminal)',
  },
  deny: {
    background: 'color-mix(in srgb, var(--pe-accent-deny) 8%, transparent)',
    borderColor: 'color-mix(in srgb, var(--pe-accent-deny) 35%, transparent)',
    color: 'var(--pe-accent-deny)',
    boxShadow: '0 0 24px color-mix(in srgb, var(--pe-accent-deny) 15%, transparent)',
    labelColor: 'var(--pe-accent-deny)',
  },
}

/**
 * Circular terminal disk used at either end of the canvas — Intake on
 * the left, Allow / Deny on the right. Width fixed at 96px wrapper
 * with a 52px round disk at center.
 *
 * When `terminal.onClick` is provided the component renders as a
 * `<button>` — keyboard-activatable via Enter and Space — with an
 * `aria-label` derived from the terminal's label. Without it the
 * component is a static `<div>` (no interactive affordance).
 *
 * Source of truth: `mockups/unified-pipeline.html` `.col-terminal`.
 */
export const PipelineTerminal = React.forwardRef<HTMLDivElement, PipelineTerminalProps>(
  function PipelineTerminal({ className, terminal, style, ...rest }, ref) {
    const tone = terminal.tone ?? 'neutral'
    const tokens = TONE_TOKENS[tone]
    const isClickable = typeof terminal.onClick === 'function'

    const diskContent = (
      <div className="flex flex-col items-center gap-2.5">
        <div
          className={cn(
            'flex h-[52px] w-[52px] items-center justify-center rounded-full border',
            isClickable &&
              'transition-shadow hover:shadow-[0_0_0_3px_color-mix(in_srgb,var(--pe-accent-terminal)_20%,transparent)]',
          )}
          style={{
            background: tokens.background,
            borderColor: tokens.borderColor,
            color: tokens.color,
            boxShadow: tokens.boxShadow,
          }}
        >
          {terminal.icon}
        </div>
        <div
          className="font-mono uppercase"
          style={{
            fontSize: 11,
            letterSpacing: '0.12em',
            color: tokens.labelColor,
          }}
        >
          {terminal.label}
        </div>
        {terminal.sub && (
          <div
            className="text-center font-mono"
            style={{
              fontSize: 9.5,
              letterSpacing: '0.05em',
              color: 'var(--pe-text-muted)',
            }}
          >
            {terminal.sub.split('\n').map((line) => (
              <span key={`${terminal.id}-sub-${line}`} className="block">
                {line}
              </span>
            ))}
          </div>
        )}
      </div>
    )

    if (isClickable) {
      return (
        /* The outer ref wrapper preserves the PipelineTerminal sizing contract
           (width: 96px, h-full, shrink-0) while the inner button provides the
           interactive affordance. Splitting them avoids making the entire 96px
           column a click target (only the disk + label area is). */
        <div
          ref={ref}
          className={cn('flex h-full shrink-0 items-center justify-center', className)}
          style={{ width: 96, minWidth: 96, ...style }}
          data-testid="pipeline-terminal"
          data-terminal-id={terminal.id}
          data-tone={tone}
        >
          <button
            type="button"
            onClick={terminal.onClick}
            aria-label={`Configure ${terminal.label}`}
            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--pe-accent-terminal)] rounded-full"
          >
            {diskContent}
          </button>
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className={cn('flex h-full shrink-0 items-center justify-center', className)}
        style={{ width: 96, minWidth: 96, ...style }}
        data-testid="pipeline-terminal"
        data-terminal-id={terminal.id}
        data-tone={tone}
        {...rest}
      >
        {diskContent}
      </div>
    )
  },
)
